import {
  createContext,
  useCallback,
  useContext,
  useMemo,
  useState,
  type ReactNode,
} from 'react'

type ToastItem = { id: number; message: string; tone: 'ok' | 'err' }

type ToastApi = {
  ok: (message: string) => void
  err: (message: string) => void
}

const ToastContext = createContext<ToastApi | null>(null)

export function ToastProvider({ children }: { children: ReactNode }) {
  const [items, setItems] = useState<ToastItem[]>([])

  const push = useCallback((message: string, tone: 'ok' | 'err') => {
    const id = Date.now() + Math.random()
    setItems((prev) => [...prev, { id, message, tone }])
    // Errors stay longer so staff can read / copy the message.
    const ms = tone === 'err' ? 8000 : 3200
    window.setTimeout(() => {
      setItems((prev) => prev.filter((t) => t.id !== id))
    }, ms)
  }, [])

  const api = useMemo<ToastApi>(
    () => ({
      ok: (m) => push(m, 'ok'),
      err: (m) => push(m, 'err'),
    }),
    [push],
  )

  return (
    <ToastContext.Provider value={api}>
      {children}
      <div className="pointer-events-none fixed right-4 bottom-4 z-[60] flex w-[min(22rem,calc(100vw-2rem))] flex-col gap-2">
        {items.map((t) => (
          <div
            key={t.id}
            role="status"
            className={`pointer-events-auto rounded-champ border px-3.5 py-2.5 text-sm shadow-mega ${
              t.tone === 'ok'
                ? 'border-vert-texte/30 bg-vert-fond text-vert-texte'
                : 'border-rouge/30 bg-rouge-fond text-rouge-texte'
            }`}
          >
            {t.message}
          </div>
        ))}
      </div>
    </ToastContext.Provider>
  )
}

export function useToast(): ToastApi {
  const ctx = useContext(ToastContext)
  if (!ctx) throw new Error('useToast hors ToastProvider')
  return ctx
}
